import React from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import { useDrawer } from '@/composables/useDrawer';
import CreateServiceRequestButton from '@/components/device/CreateServiceRequestButton.jsx';
import {
    ActivateDeviceActionButton,
    DeactivateDeviceActionButton,
    RestartDeviceActionButton,
    UnregisterDeviceActionButton,
} from '@/components/global/buttons/actionButtons.jsx';
import { ShowDeviceConfigButton } from '@/components/device/buttons/ShowDeviceConfigButton.jsx';
import { canViewDeviceConfig } from '@/helpers/device/deviceConfig.js';

const DeviceMoreActionsPopover = ({ record, buttonLabel }) => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();

    if (isLoading || !record) return null;

    const showConfig = canViewDeviceConfig(permissions);

    const hasAssignedCollectionPoint = !!(record.collectionPointId ?? record.collectionPoint?.id);
    const openCreateDrawer = () => {
        openDrawer(record.id, 'service-request-create');
    };

    return (
        <CustomMorePopover record={record} buttonLabel={buttonLabel}>
            <CustomShowModalButton onOpen={() => openDrawer(record.id, 'device-show')} label={translate('resources.device.labels.showDevice')} />
            {(permissions.isAdmin || permissions.isServiceMaster) && (
                <>
                    {permissions.isAdmin && (
                        <CustomEditModalButton onOpen={() => openDrawer(record.id, 'device-edit')} label={translate('resources.device.labels.editDevice')} />
                    )}
                    {hasAssignedCollectionPoint && (
                        <CreateServiceRequestButton onClick={openCreateDrawer} label={translate('resources.device.labels.createServiceRequest')} />
                    )}
                    <RestartDeviceActionButton
                        id={record.id}
                        label={translate('resources.device.labels.restartDevice')}
                        buttonProps={{ 'data-keep-popover-open': true }}
                    />
                    {record.registrationDate && ['Kaucja', 'Reselekt'].includes(record.collectionPoint?.operator) && (
                        <UnregisterDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.unregisterDevice')}
                            buttonProps={{ 'data-keep-popover-open': true }}
                            operator={record.collectionPoint.operator}
                            registrationDate={record.registrationDate}
                        />
                    )}
                    {record.isActive ? (
                        <DeactivateDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.deactivateDevice')}
                            buttonProps={{ 'data-keep-popover-open': true }}
                        />
                    ) : (
                        <ActivateDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.activateDevice')}
                            buttonProps={{ 'data-keep-popover-open': true }}
                        />
                    )}
                </>
            )}
            {showConfig && <ShowDeviceConfigButton id={record.id} />}
        </CustomMorePopover>
    );
};

DeviceMoreActionsPopover.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        isActive: PropTypes.bool,
        registrationDate: PropTypes.string,
        collectionPointId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        collectionPoint: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            operator: PropTypes.string,
        }),
    }),
    buttonLabel: PropTypes.string,
};

export default DeviceMoreActionsPopover;
